import {
  ConversationStarter,
  SaveConversationRequest,
  ConversationApiResponse
} from '@/types/conversation';

/**
 * Save a new conversation starter
 */
export async function saveConversationStarter(conversationData: SaveConversationRequest): Promise<ConversationStarter> {
  const response = await fetch('/.netlify/functions/saveConversationStarter', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(conversationData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to save conversation');
  }

  return response.json();
}

/**
 * Fetch curated conversations for a session
 */
export async function getCuratedConversations(
  sessionId: string,
  options: {
    limit?: number;
    offset?: number;
    status?: 'all' | 'ready' | 'used';
    sortBy?: 'newest' | 'oldest';
  } = {}
): Promise<ConversationApiResponse> {
  const params = new URLSearchParams({
    sessionId,
    limit: (options.limit || 50).toString(),
    offset: (options.offset || 0).toString(),
  });

  if (options.status && options.status !== 'all') {
    params.append('status', options.status);
  }

  if (options.sortBy) {
    params.append('sortBy', options.sortBy);
  }

  const response = await fetch(`/.netlify/functions/getCuratedConversations?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch conversations');
  }

  return response.json();
}

/**
 * Delete a conversation starter
 */
export async function deleteConversationStarter(conversationId: string): Promise<void> {
  const response = await fetch('/.netlify/functions/deleteConversationStarter', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: conversationId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to delete conversation');
  }
}

/**
 * Update conversation activation status
 */
export async function updateConversationStatus(
  conversationId: string, 
  isActivated: boolean
): Promise<ConversationStarter> {
  const response = await fetch('/.netlify/functions/updateConversationStatus', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: conversationId, isActivated }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to update conversation status');
  }

  return response.json();
}

// Session management is handled by the ConversationCuration library
export { getOrCreateSessionId, generateSessionId } from '@/lib/ConversationCuration/sessionManager';